import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import queryString from 'query-string';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import Helmet from 'helmet/helmet';
import Styled from 'styled';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import bazaarSVG from 'four-nets/icons/svg/icon_bazaar.svg';
import newArticle from 'four-nets/icons/svg/icon_new-article.svg';
import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';
import TitleView from 'four-nets/ui/title-view';
import BigButtonView from 'four-nets/ui/big-button/big-button-view';
import { HideForUnpaidVendor } from 'four-nets/vendor/permissions';

import { SiteContext } from 'four-nets';
import CreateEditProductView, {
    CreateEditProductViewModel,
} from 'four-nets/bazaar/product/create-edit-product/create-edit-product-view';
import CategoriesPanelView from 'four-nets/bazaar/categories/categories-panel/categories-panel-view';
import ProductsFilterBarView from 'four-nets/bazaar/product-lists/products-filter-bar-view';
import { SuperCategoryStore } from 'four-nets/bazaar/bazaar';
import PropertiesFilters from 'four-nets/bazaar/properties-filter/properties-filters';

import FilterPanelView from './filter-panel/filter-panel-view';
import CategoriesFiltersLoader from './categories-filters/categories-filters-loader';
import { CheckboxesMobile } from './filter-panel/checkboxes/checkboxes';

import query, { ActiveCategoryQuery, ActiveCategoryQueryVariables } from './active-category.graphql';

import titleStyles from './title-view.scss';
import defaultStyles from './categories-view.scss';

enum Exclude {
    VENDOR = 'VENDOR',
    USED = 'USED',
}

export interface Props {
    styles: typeof defaultStyles;

    slug?: string;
    county?: string | string[];
    price?: string;
    hasActiveFilters?: boolean;

    productStore: any;
    authCheck: (action: () => Promise<any>, origin: AuthOrigin) => Promise<any>;
}

const CategoriesView: React.FC<Props> = props => {
    const { styles, productStore, authCheck, county, slug } = props;

    const history = useHistory();
    const location = useLocation();
    const search = useMemo(() => queryString.parse(location.search), [location]);

    const site = useContext(SiteContext);

    const { data } = useQuery<ActiveCategoryQuery, ActiveCategoryQueryVariables>(query, {
        variables: { slug },
        skip: !slug,
    });
    const activeCategory = data?.serverCategoryBySlug;

    const [excludeVendor, setExcludeVendor] = useState(Boolean(queryString.parse(location.search).excludeVendor));
    const [excludeUsed, setExcludeUsed] = useState(Boolean(queryString.parse(location.search).excludeUsed));

    const newProductView = useRef<any>(null);

    const myBazaarLink = useMemo(() => '/market/my_bazaar/', []);
    const createEditProductViewModel = useMemo(
        () =>
            new CreateEditProductViewModel({
                productStore,
                superCategoryStore: new SuperCategoryStore({
                    api: productStore?.api,
                    userStore: productStore?.userStore,
                }),
                site,
            }),
        [productStore, site]
    );

    useEffect(() => {
        const { excludeUsed: _unused1, excludeVendor: _unused2, ...rest } = queryString.parse(location.search);

        const exclude: Partial<{ excludeUsed: boolean; excludeVendor: boolean }> = {};
        if (excludeUsed) {
            exclude.excludeUsed = true;
        }
        if (excludeVendor) {
            exclude.excludeVendor = true;
        }

        history.push({
            pathname: location.pathname,
            search: `?${queryString.stringify({ ...rest, ...exclude, page: 1 })}`,
        });
    }, [excludeVendor, excludeUsed]);

    const setNewProductView = (ref: any) => {
        newProductView.current = ref;
    };

    const onNewProductClick = () => {
        if (newProductView.current) {
            const openCreateEditForm = () => {
                newProductView.current.show({
                    form: createEditProductViewModel.setupForm(),
                });
                return Promise.resolve();
            };
            authCheck(openCreateEditForm, AuthOrigin.NewBazaarProduct);
        }
    };

    const navigate = (parameterName: string, selectedFields: string[] | number[] | number | string) => {
        const { page } = queryString.parse(location.search);
        history.push({
            pathname: location.pathname,
            search: `?${queryString.stringify({
                ...queryString.parse(location.search),
                [parameterName]: selectedFields,
                page: page ? 1 : undefined,
            })}`,
        });
    };

    const confirmCounty = (selectedFields: string[] | number[] | number | string) => {
        navigate('county', selectedFields);
    };

    const confirmPrice = (selectedFields: string[] | number[] | number | string) => {
        navigate('price', selectedFields);
    };

    const navigateClear = (parameterName: string) => {
        history.push({
            pathname: location.pathname,
            search: `?${queryString.stringify({
                ...queryString.parse(location.search),
                [parameterName]: undefined,
            })}`,
        });
    };

    const clearCounty = () => {
        navigateClear('county');
    };

    const clearPrice = () => {
        navigateClear('price');
    };

    const onCheckboxChange = (exclude: Exclude) => () => {
        if (exclude === Exclude.USED) {
            setExcludeUsed(prev => !prev);
        } else {
            setExcludeVendor(prev => !prev);
        }
    };
    const onVendorChange = onCheckboxChange(Exclude.VENDOR);
    const onUsedChange = onCheckboxChange(Exclude.USED);

    const renderHelmet = (H: React.ComponentClass) => {
        const categoryPart = activeCategory?.name ? `${activeCategory?.name} - ` : '';

        if (site.isLiving) {
            return (
                <FormattedMessage id='BAZAAR'>
                    {bazaarTitle => (
                        <FormattedMessage id='BAZAAR_SEO_LIVING'>
                            {title => (
                                <FormattedMessage id='BAZAAR_INDEX_DESCRIPTION_LIVING'>
                                    {descLiving => {
                                        const subtitle = county
                                            ? Array.isArray(county)
                                                ? `${county.join(', ')} | `
                                                : `${county} | `
                                            : bazaarTitle;
                                        const pageTitle = `${categoryPart}${subtitle}${site.name}`;
                                        return (
                                            <H>
                                                <title>{pageTitle}</title>
                                                <meta
                                                    property='og:title'
                                                    content={`${title as string} | ${site.name}`}
                                                />
                                                <meta name='Description' content={descLiving as string} />
                                            </H>
                                        );
                                    }}
                                </FormattedMessage>
                            )}
                        </FormattedMessage>
                    )}
                </FormattedMessage>
            );
        } else if (site.isWedding) {
            return (
                <FormattedMessage id='BAZAAR_SEO_WEDDING'>
                    {title => (
                        <FormattedMessage id='BAZAAR_INDEX_DESCRIPTION_WEDDING'>
                            {descWedding => {
                                const subtitle = county
                                    ? Array.isArray(county)
                                        ? `${county.join(', ')} | `
                                        : `${county} | `
                                    : title;
                                const pageTitle = `${categoryPart}${subtitle}${site.name}`;
                                return (
                                    <H>
                                        <title>{pageTitle}</title>
                                        <meta property='og:title' content={`${title as string} | ${site.name}`} />
                                        <meta name='Description' content={descWedding as string} />
                                    </H>
                                );
                            }}
                        </FormattedMessage>
                    )}
                </FormattedMessage>
            );
        }
        return (
            <FormattedMessage id='BAZAAR_SEO_GARDEN'>
                {title => (
                    <FormattedMessage id='BAZAAR_INDEX_DESCRIPTION_GARDEN'>
                        {descGarden => {
                            const subtitle = county
                                ? Array.isArray(county)
                                    ? `${county.join(', ')} | `
                                    : `${county} | `
                                : title;
                            const pageTitle = `${categoryPart}${subtitle}${site.name}`;
                            return (
                                <H>
                                    <title>{pageTitle}</title>
                                    <meta property='og:title' content={`${title as string} | ${site.name}`} />
                                    <meta name='Description' content={descGarden as string} />
                                </H>
                            );
                        }}
                    </FormattedMessage>
                )}
            </FormattedMessage>
        );
    };

    return (
        <div className={styles.categories}>
            <div className={styles.titleWrapper}>
                <TitleView
                    styles={titleStyles}
                    title={
                        <h1 className={styles.title}>
                            <FormattedMessage id='BAZAAR' />
                        </h1>
                    }
                    leftComponent={
                        myBazaarLink ? (
                            <Link to={myBazaarLink} className={styles.myBazaarLink}>
                                <SVGIcon style={{ image: styles.myBazaarIcon }} src={bazaarSVG} />
                                <FormattedMessage id='BAZAAR_MY_PRODUCTS' />
                            </Link>
                        ) : null
                    }
                    rightComponent={
                        <HideForUnpaidVendor>
                            <div>
                                <BigButtonView
                                    icon={newArticle}
                                    label={<FormattedMessage id='BAZAAR_ADD_PRODUCT' />}
                                    onClick={onNewProductClick}
                                />
                            </div>
                        </HideForUnpaidVendor>
                    }
                />
                <CreateEditProductView
                    innerRef={setNewProductView}
                    createEditProductViewModel={createEditProductViewModel}
                />
            </div>
            <div className={styles.columnsWrapper}>
                <FilterPanelView
                    activeCategory={activeCategory}
                    openHeight='auto'
                    onVendorChange={onVendorChange}
                    onUsedChange={onUsedChange}
                    excludeUsed={excludeUsed}
                    excludeVendor={excludeVendor}
                >
                    <CategoriesPanelView />
                </FilterPanelView>
                <CheckboxesMobile
                    onVendorChange={onVendorChange}
                    onUsedChange={onUsedChange}
                    excludeUsed={excludeUsed}
                    excludeVendor={excludeVendor}
                />
            </div>
            <div className={styles.divider}>
                <ProductsFilterBarView orderBy={search.orderBy}>
                    <CategoriesFiltersLoader
                        initialCountiesValue={props.county}
                        initialPriceValue={props.price}
                        confirmCounty={confirmCounty}
                        confirmPrice={confirmPrice}
                        clearCounty={clearCounty}
                        clearPrice={clearPrice}
                    />
                    {activeCategory ? <PropertiesFilters categorySlug={activeCategory.slug} /> : null}
                </ProductsFilterBarView>
            </div>
            <Helmet render={renderHelmet} />
        </div>
    );
};

export default Styled(defaultStyles)(CategoriesView);
